type WebMcpToolResult = {
  content: Array<{ type: "text"; text: string }>;
  structuredContent?: unknown;
  isError?: boolean;
};

type WebMcpTool = {
  name: string;
  title: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: { readOnlyHint?: boolean };
  execute: (input: Record<string, unknown>) => WebMcpToolResult | Promise<WebMcpToolResult>;
};

type ModelContextLike = {
  registerTool?: (
    tool: WebMcpTool,
    options?: { signal?: AbortSignal },
  ) => void | Promise<void>;
  provideContext?: (context: { tools: WebMcpTool[] }) => void | Promise<void>;
};

const SITE_PAGES = [
  { path: "/", title: "Home", description: "Overview of eSchool Ethiopia." },
  { path: "/platform", title: "Platform", description: "Connected eSchool applications and platform architecture." },
  { path: "/features", title: "Features", description: "School management capabilities and modules." },
  { path: "/admin-dashboard", title: "Admin Dashboard", description: "Administration, records, fees, staff, and reports." },
  { path: "/teacher-app", title: "Teacher App", description: "Attendance, grading, homework, and communication." },
  { path: "/parent-app", title: "Parent App", description: "Attendance, results, fees, and school updates for parents." },
  { path: "/student-app", title: "Student App", description: "Timetables, assignments, results, and announcements." },
  { path: "/pricing", title: "Pricing", description: "Published eSchool plans and included services." },
  { path: "/about", title: "About", description: "eSchool and Dream Tech PLC." },
  { path: "/blog", title: "Blog", description: "School operations and education technology articles." },
  { path: "/contact", title: "Contact", description: "Official contact details and free demo request." },
  { path: "/register-school", title: "Register a school", description: "Apply for the limited eSchool onboarding program." },
] as const;

const PRICING = [
  { name: "Starter", studentRange: "Fewer than 400", studentPriceEtb: 250, staffPriceEtb: 350, setupFeeEtb: 150000 },
  { name: "Growth", studentRange: "400 to 1,000", studentPriceEtb: 200, staffPriceEtb: 300, setupFeeEtb: 200000 },
  { name: "Enterprise", studentRange: "More than 1,000", studentPriceEtb: 150, staffPriceEtb: 250, setupFeeEtb: 250000 },
] as const;

const result = (data: unknown): WebMcpToolResult => ({
  content: [{ type: "text", text: JSON.stringify(data, null, 2) }],
  structuredContent: data,
});

const tools: WebMcpTool[] = [
  {
    name: "get_eschool_overview",
    title: "Get eSchool overview",
    description: "Return a concise public overview and official contact details for eSchool Ethiopia.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
    annotations: { readOnlyHint: true },
    execute: () =>
      result({
        name: "eSchool Ethiopia",
        provider: "Dream Tech PLC",
        description:
          "A cloud-based school management platform for attendance, fees, academics, communication, staff, parents, and students.",
        email: "info@eschool.et",
        phone: "+251 966 778 811",
        location: "Addis Ababa, Ethiopia",
        demoUrl: "https://eschool.et/contact",
      }),
  },
  {
    name: "list_eschool_pages",
    title: "List eSchool pages",
    description: "List the principal public pages available on the eSchool website.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
    annotations: { readOnlyHint: true },
    execute: () => result(SITE_PAGES),
  },
  {
    name: "get_eschool_pricing",
    title: "Get eSchool pricing",
    description: "Return published eSchool pricing in Ethiopian birr.",
    inputSchema: { type: "object", properties: {}, additionalProperties: false },
    annotations: { readOnlyHint: true },
    execute: () => result({ currency: "ETB", plans: PRICING }),
  },
  {
    name: "open_eschool_page",
    title: "Open an eSchool page",
    description: "Navigate this browser tab to a selected public page on eschool.et.",
    inputSchema: {
      type: "object",
      properties: {
        path: {
          type: "string",
          enum: SITE_PAGES.map(({ path }) => path),
          description: "The site-relative page path to open.",
        },
      },
      required: ["path"],
      additionalProperties: false,
    },
    execute: ({ path }) => {
      const page = SITE_PAGES.find((candidate) => candidate.path === path);
      if (!page) {
        return {
          content: [{ type: "text", text: "The requested path is not an allowed eSchool page." }],
          isError: true,
        };
      }

      window.location.assign(page.path);
      return result({ opened: page.path, title: page.title });
    },
  },
];

const getModelContext = ():
  | { modelContext: ModelContextLike; preferProvideContext: boolean }
  | undefined => {
  const documentContext = (document as Document & { modelContext?: ModelContextLike }).modelContext;
  if (documentContext) {
    return { modelContext: documentContext, preferProvideContext: false };
  }

  const navigatorContext = (navigator as Navigator & { modelContext?: ModelContextLike }).modelContext;
  if (!navigatorContext) return undefined;

  return {
    modelContext: navigatorContext,
    preferProvideContext: Boolean(navigatorContext.provideContext),
  };
};

export const registerWebMcpTools = () => {
  const resolvedContext = getModelContext();
  if (!resolvedContext) return () => undefined;

  const { modelContext, preferProvideContext } = resolvedContext;

  const controller = new AbortController();

  if (preferProvideContext && modelContext.provideContext) {
    void Promise.resolve(modelContext.provideContext({ tools })).catch(() => {
      // Ignore incompatible preview implementations without affecting the website.
    });
  } else if (modelContext.registerTool) {
    for (const tool of tools) {
      void Promise.resolve(modelContext.registerTool(tool, { signal: controller.signal })).catch(() => {
        // WebMCP is experimental; a browser may expose a partially compatible preview API.
      });
    }
  } else if (modelContext.provideContext) {
    void Promise.resolve(modelContext.provideContext({ tools })).catch(() => {
      // Ignore incompatible preview implementations without affecting the website.
    });
  }

  return () => {
    controller.abort();
    if ((preferProvideContext || !modelContext.registerTool) && modelContext.provideContext) {
      void Promise.resolve(modelContext.provideContext({ tools: [] })).catch(() => undefined);
    }
  };
};

export { tools as webMcpTools };
